import PaymentMethodCardType from 'store/enums/PaymentMethodCardType';
import { PaymentFormValues } from 'store/types/FormValues';

export const cardFields = {
  number: 'card-number',
  securityCode: 'security-code',
  expiryMonth: 'expiry-month',
  expiryYear: 'expiry-year',
  nameOnCard: 'cardholder-name',
};

export const citiValidityFieldNames = [
  'card.number',
  'card.nameOnCard',
  'card.expiryMonth',
  'card.expiryYear',
  'card.securityCode',
] as const;

export const citiHostedFieldSelectors = [
  `#${cardFields.number}`,
  `#${cardFields.nameOnCard}`,
  `#${cardFields.expiryMonth}`,
  `#${cardFields.expiryYear}`,
  `#${cardFields.securityCode}`,
] as const;

export type CitiValidityFieldName = typeof citiValidityFieldNames[number];

export const citiFieldNameToHostedSelector: Record<CitiValidityFieldName, CitiHostedFieldSelector> = {
  'card.number': '#card-number',
  'card.nameOnCard': '#cardholder-name',
  'card.expiryMonth': '#expiry-month',
  'card.expiryYear': '#expiry-year',
  'card.securityCode': '#security-code',
};

export const normalizeCitiValiditySelector = (selector: string): CitiHostedFieldSelector | null => {
  if (citiHostedFieldSelectors.includes(selector as CitiHostedFieldSelector)) {
    return selector as CitiHostedFieldSelector;
  }

  return citiFieldNameToHostedSelector[selector as CitiValidityFieldName] ?? null;
};

export type CitiHostedFieldSelector = typeof citiHostedFieldSelectors[number];

export type CitiValidityErrorReason =
  | 'INVALID_VALUE'
  | 'MISSING_VALUE'
  | 'NOT_SUPPORTED'
  | 'INVALID_LENGTH'
  | 'INVALID_CARD_NUMBER'
  | 'INVALID_CARD_SECURITY_CODE'
  | 'INVALID_CARD_EXPIRY_MONTH'
  | 'INVALID_CARD_EXPIRY_YEAR'
  | string;

export type CitiFieldValidityResult = {
  isValid?: boolean;
  isIncomplete?: boolean;
  hasError?: boolean;
  errorReason?: CitiValidityErrorReason;
};

export type FieldValidationState = Record<
  CitiHostedFieldSelector,
  {
    touched: boolean;
    isValid: boolean;
    isIncomplete: boolean;
    hasError: boolean;
    errorReason: string | null;
    message: string | null;
  }
>;

export const initialFieldValidationState: FieldValidationState = citiHostedFieldSelectors.reduce((acc, selector) => {
  acc[selector] = {
    touched: false,
    isValid: false,
    isIncomplete: true,
    hasError: false,
    errorReason: null,
    message: null,
  };

  return acc;
}, {} as FieldValidationState);

export const getIncompleteFieldMessage = (selector: CitiHostedFieldSelector): string => {
  switch (selector) {
    case '#card-number':
      return 'Enter your card number.';
    case '#cardholder-name':
      return 'Enter the cardholder name.';
    case '#expiry-month':
      return 'Enter the expiration month.';
    case '#expiry-year':
      return 'Enter the expiration year.';
    case '#security-code':
      return 'Enter the security code.';
    default:
      return 'Complete this field.';
  }
};

export const getInvalidFieldMessage = (selector: CitiHostedFieldSelector, errorReason?: string): string => {
  switch (errorReason) {
    case 'MISSING_VALUE':
      return getIncompleteFieldMessage(selector);
    case 'NOT_SUPPORTED':
      if (selector === '#card-number') {
        return 'This card type is not supported. Please use a different card.';
      }
      return 'This value is not supported.';
    case 'INVALID_LENGTH':
      if (selector === '#card-number') {
        return 'Enter a valid card number.';
      }
      if (selector === '#security-code') {
        return 'Enter a valid security code.';
      }
      return 'Enter a valid value.';
    case 'INVALID_CARD_NUMBER':
      return 'Enter a valid card number.';
    case 'INVALID_CARD_SECURITY_CODE':
      return 'Enter a valid security code.';
    case 'INVALID_CARD_EXPIRY_MONTH':
      return 'Enter a valid expiration month.';
    case 'INVALID_CARD_EXPIRY_YEAR':
      return 'Enter a valid expiration year.';
    case 'INVALID_VALUE':
      switch (selector) {
        case '#card-number':
          return 'Enter a valid card number.';
        case '#cardholder-name':
          return 'Enter a valid cardholder name.';
        case '#expiry-month':
          return 'Enter a valid expiration month.';
        case '#expiry-year':
          return 'Enter a valid expiration year.';
        case '#security-code':
          return 'Enter a valid security code.';
        default:
          return 'Enter a valid value.';
      }

    default:
      return 'Enter a valid value.';
  }
};

export const getCitiFieldErrorMessage = (
  selector: CitiHostedFieldSelector,
  result: CitiFieldValidityResult
): string => {
  if (result.isIncomplete) {
    return getIncompleteFieldMessage(selector);
  }

  return getInvalidFieldMessage(selector, result.errorReason);
};

export type PendingSubmit = {
  resolve: (values: Partial<PaymentFormValues>) => void;
  reject: (error: Error) => void;
};

export const isSuccessStatus = (status?: string): boolean => {
  return status === 'ok' || status === 'OK';
};

export const isFieldsInErrorStatus = (status?: string): boolean => {
  return status === 'fields_in_error' || status === 'FIELDS_IN_ERROR';
};

export const normalizeCardBrand = (brand: string): PaymentMethodCardType => {
  switch (brand.toLowerCase()) {
    case 'visa':
      return PaymentMethodCardType.Visa;
    case 'mastercard':
      return PaymentMethodCardType.Mastercard;
    case 'amex':
      return PaymentMethodCardType.Amex;
    case 'discover':
      return PaymentMethodCardType.Discover;
    case 'diners_club':
      return PaymentMethodCardType.Diners;
    case 'maestro':
      return PaymentMethodCardType.Maestro;
    case 'jcb':
      return PaymentMethodCardType.Jcb;
    default:
      throw new Error(`Unsupported card brand: ${brand}`);
  }
};
